/**
 * Created by neo on 10.01.2026
 * Challenge Map Component - Shows Google Maps with route polylines and location markers
 */
import * as React from 'react';
import { useMemo, useCallback, useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { APIProvider, Map, AdvancedMarker, Pin, InfoWindow, useMap } from '@vis.gl/react-google-maps';
import { decode } from '@googlemaps/polyline-codec';
import dayjs from 'dayjs';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { useLocalized } from '../../../Hooks/useLocalized';
import { RouteChallenge } from '../../../Model/Engagement/RouteChallenge/RouteChallenge';
import { IndividualChallenge } from '../../../Model/Engagement/IndividualChallenge/IndividualChallenge';
import { RouteChallengeLocation } from '../../../Model/Engagement/RouteChallenge/RouteChallengeLocation';
import { RouteChallengeTeamReachedLocation } from '../../../Model/Engagement/RouteChallenge/RouteChallengeTeamReachedLocation';
import { CommunityWallPost, createCommunityWallPost, CommunityWallPostApi } from '../../../Model/Engagement/CommunityWall/CommunityWallPost';
import { Theme } from '../../../Theme/Theme';
import { getMediaMediumOrSmallest } from '../../../Model/Media/Media';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyCptdMtGLKz0F8FHU-uAWMqk0ydVnz96uw';

const MapContainer = styled.div`
  width: 100%;
  height: 400px;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  @media (min-width: 768px) {
    height: 450px;
  }
`;

const MapHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const MapTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #86868b;
  letter-spacing: 1px;
  text-transform: uppercase;
`;

const MapToggle = styled.button<{ isActive?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${props => props.isActive ? Theme.Colors.primary : 'white'};
  color: ${props => props.isActive ? 'white' : Theme.Colors.textColor};
  border: 1px solid ${props => props.isActive ? Theme.Colors.primary : Theme.Colors.lightestGrey};
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.isActive ? Theme.Colors.primaryDark : Theme.Colors.lightestGrey};
  }
`;

const MapWrapper = styled.div`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
`;

const MapOverlay = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 10;
  display: flex;
  gap: 8px;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 10px 14px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StatEmoji = styled.span`
  font-size: 20px;
`;

const StatContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatValue = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
  line-height: 1.2;
`;

const StatLabel = styled.div`
  font-size: 10px;
  color: ${Theme.Colors.warmGrey};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MapControls = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MapControlButton = styled.button<{ isActive?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${props => props.isActive ? Theme.Colors.primary : 'rgba(255, 255, 255, 0.95)'};
  backdrop-filter: blur(10px);
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: all 0.2s;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const MapHint = styled.div`
  position: absolute;
  bottom: 12px;
  left: 12px;
  right: 12px;
  z-index: 10;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
`;

const MapHintText = styled.span`
  font-size: 12px;
  color: #86868b;
`;

const MapHintDot = styled.span`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #86868b;
`;


const NoMapFallback = styled.div`
  width: 100%;
  height: 400px;
  border-radius: 16px;
  background: linear-gradient(135deg, ${Theme.Colors.lightestGrey} 0%, ${Theme.Colors.lighterGrey} 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  color: ${Theme.Colors.charcoal};
`;

const NoMapEmoji = styled.span`
  font-size: 48px;
  margin-bottom: 12px;
`;

const NoMapText = styled.span`
  font-size: 14px;
`;

const pulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(0, 221, 163, 0.4); }
  70% { transform: scale(1.05); box-shadow: 0 0 0 10px rgba(0, 221, 163, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(0, 221, 163, 0); }
`;

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
`;

const LocationMarkerWrapper = styled.div<{ isFirst?: boolean; isLast?: boolean; isReached?: boolean; hasPosts?: boolean }>`
  position: relative;
  cursor: pointer;
  transition: transform 0.2s;
  
  &:hover {
    transform: scale(1.1);
  }
`;

const LocationMarkerPin = styled.div<{ isFirst?: boolean; isLast?: boolean; isReached?: boolean; hasPosts?: boolean }>`
  width: 44px;
  height: 44px;
  border-radius: 50% 50% 50% 0;
  transform: rotate(-45deg);
  background: ${(props) => 
    props.isFirst ? 'linear-gradient(135deg, #52c41a, #389e0d)' :
    props.isLast ? 'linear-gradient(135deg, #f5222d, #cf1322)' :
    props.isReached ? `linear-gradient(135deg, ${Theme.Colors.primary}, ${Theme.Colors.primaryDark})` :
    'linear-gradient(135deg, #8c8c8c, #595959)'
  };
  border: 3px solid white;
  box-shadow: 0 3px 10px rgba(0,0,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${(props) => props.hasPosts ? bounce : 'none'} 2s infinite;
`;

const LocationMarkerEmoji = styled.span`
  transform: rotate(45deg);
  font-size: 18px;
`;

const LocationMarkerIndex = styled.span`
  transform: rotate(45deg);
  font-size: 14px;
  font-weight: 700;
  color: white;
`;

const PostsBadge = styled.div`
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 22px;
  height: 22px;
  border-radius: 11px;
  background: #ff4d4f;
  border: 2px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: white;
  box-shadow: 0 2px 6px rgba(0,0,0,0.2);
  padding: 0 4px;
`;

const LocationPopup = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 12px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.2);
  width: 280px;
  max-height: 350px;
  overflow: hidden;
  z-index: 100;
`;

const PopupHeader = styled.div<{ imageUrl?: string }>`
  height: 100px;
  background: ${(props) => props.imageUrl 
    ? `url(${props.imageUrl})` 
    : `linear-gradient(135deg, ${Theme.Colors.primary}, ${Theme.Colors.primaryDark})`
  };
  background-size: cover;
  background-position: center;
  position: relative;
  display: flex;
  align-items: flex-end;
  padding: 12px;
`;

const PopupTitle = styled.div`
  color: white;
  font-size: 16px;
  font-weight: 700;
  text-shadow: 0 1px 3px rgba(0,0,0,0.5);
`;

const PopupContent = styled.div`
  padding: 12px;
  max-height: 200px;
  overflow-y: auto;
`;

const PopupPostPreview = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid ${Theme.Colors.lightestGrey};
  
  &:last-child {
    border-bottom: none;
  }
`;

const PopupPostImage = styled.div<{ imageUrl?: string }>`
  width: 50px;
  height: 50px;
  border-radius: 8px;
  background: ${(props) => props.imageUrl ? `url(${props.imageUrl})` : Theme.Colors.lightestGrey};
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
`;

const PopupPostContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const PopupPostAuthor = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
`;

const PopupPostText = styled.div`
  font-size: 12px;
  color: ${Theme.Colors.warmGrey};
  margin-top: 2px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PopupPostTime = styled.div`
  font-size: 10px;
  color: ${Theme.Colors.lighterGrey};
  margin-top: 4px;
`;

const PopupEmptyPosts = styled.div`
  text-align: center;
  padding: 20px;
  color: ${Theme.Colors.warmGrey};
  font-size: 13px;
`;

const PopupViewAll = styled.button`
  width: 100%;
  padding: 12px;
  border: none;
  background: ${Theme.Colors.lightestGrey};
  color: ${Theme.Colors.primary};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: ${Theme.Colors.lighterGrey};
  }
`;

const PopupClose = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 12px;
  background: rgba(0,0,0,0.4);
  border: none;
  color: white;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(0,0,0,0.6);
  }
`;

const TeamMarker = styled.div<{ isMyTeam?: boolean }>`
  width: ${(props) => props.isMyTeam ? '48px' : '38px'};
  height: ${(props) => props.isMyTeam ? '48px' : '38px'};
  border-radius: 50%;
  background: ${(props) => props.isMyTeam 
    ? `linear-gradient(135deg, ${Theme.Colors.primary}, ${Theme.Colors.primaryDark})` 
    : 'linear-gradient(135deg, #00C3FF, #0094C1)'
  };
  border: ${(props) => props.isMyTeam ? '4px' : '3px'} solid white;
  box-shadow: 0 3px 12px rgba(0,0,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${(props) => props.isMyTeam ? '20px' : '16px'};
  animation: ${(props) => props.isMyTeam ? pulse : 'none'} 2s infinite;
  cursor: pointer;
  transition: transform 0.2s;
  
  &:hover {
    transform: scale(1.15);
  }
`;

const ParticipantMarker = styled.div<{ isMe?: boolean }>`
  width: ${(props) => props.isMe ? '42px' : '32px'};
  height: ${(props) => props.isMe ? '42px' : '32px'};
  border-radius: 50%;
  background: ${(props) => props.isMe 
    ? `linear-gradient(135deg, ${Theme.Colors.primary}, ${Theme.Colors.primaryDark})` 
    : 'linear-gradient(135deg, #722ed1, #531dab)'
  };
  border: ${(props) => props.isMe ? '3px' : '2px'} solid white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${(props) => props.isMe ? '18px' : '14px'};
  animation: ${(props) => props.isMe ? pulse : 'none'} 2s infinite;
  cursor: pointer;
  transition: transform 0.2s;
  
  &:hover {
    transform: scale(1.15);
  }
`;

type LocationPostsMapType = globalThis.Map<string, CommunityWallPost[]>;

type ChallengeMapProps = {
  challenge: RouteChallenge | IndividualChallenge;
  locations: RouteChallengeLocation[];
  reachedLocations?: RouteChallengeTeamReachedLocation[];
  language: string;
  onLocationClick?: (location: RouteChallengeLocation, index: number) => void;
  myTeamPosition?: { lat: number; lng: number };
  myParticipantPosition?: { lat: number; lng: number };
  teams?: Array<{ id: string; name: string; position: { lat: number; lng: number }; points: number }>;
  participants?: Array<{ id: string; name: string; position: { lat: number; lng: number }; points: number }>;
};

const averageGeolocation = (
  coords: { lat: number; lng: number }[],
): { center: { lat: number; lng: number }; latDelta: number; lngDelta: number } => {
  if (coords.length === 0) {
    return { center: { lat: 47.3732, lng: 8.5283 }, latDelta: 2, lngDelta: 2 };
  }

  if (coords.length === 1) {
    return { center: coords[0], latDelta: 0.05, lngDelta: 0.05 };
  }

  const lats = coords.map((c) => c.lat);
  const lngs = coords.map((c) => c.lng);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);

  const latRange = maxLat - minLat;
  const lngRange = maxLng - minLng;

  // Calculate center using spherical coordinates for accuracy
  let x = 0, y = 0, z = 0;
  for (const coord of coords) {
    const latRad = (coord.lat * Math.PI) / 180;
    const lngRad = (coord.lng * Math.PI) / 180;
    x += Math.cos(latRad) * Math.cos(lngRad);
    y += Math.cos(latRad) * Math.sin(lngRad);
    z += Math.sin(latRad);
  }
  x /= coords.length;
  y /= coords.length;
  z /= coords.length;

  const centralLng = Math.atan2(y, x);
  const centralLat = Math.atan2(z, Math.sqrt(x * x + y * y));

  return {
    center: {
      lat: (centralLat * 180) / Math.PI,
      lng: (centralLng * 180) / Math.PI,
    },
    latDelta: Math.max(latRange * 1.3, 0.02),
    lngDelta: Math.max(lngRange * 1.3, 0.02),
  };
};

type LocationMarkerProps = {
  location: RouteChallengeLocation;
  index: number;
  totalLocations: number;
  isReached: boolean;
  posts: CommunityWallPost[];
  language: string;
  isPopupOpen: boolean;
  onTogglePopup: () => void;
  onViewAllPosts: () => void;
};

const LocationMarkerComponent: React.FC<LocationMarkerProps> = ({
  location,
  index,
  totalLocations,
  isReached,
  posts,
  language,
  isPopupOpen,
  onTogglePopup,
  onViewAllPosts,
}) => {
  const { t } = useTrans();
  const isFirst = index === 0;
  const isLast = index === totalLocations - 1;
  const hasPosts = posts.length > 0;
  const locationName = useLocalized(location.name) || `Location ${index + 1}`;
  const locationImage = location.image ? getMediaMediumOrSmallest(location.image) : undefined;

  return (
    <LocationMarkerWrapper isFirst={isFirst} isLast={isLast} isReached={isReached} hasPosts={hasPosts}>
      {isPopupOpen && (
        <LocationPopup onClick={(e) => e.stopPropagation()}>
          <PopupHeader imageUrl={locationImage}>
            <PopupClose onClick={onTogglePopup}>✕</PopupClose>
            <PopupTitle>{locationName}</PopupTitle>
          </PopupHeader>
          <PopupContent>
            {posts.length > 0 ? (
              posts.slice(0, 3).map((post) => (
                <PopupPostPreview key={post.id}>
                  <PopupPostImage imageUrl={post.firstMedia ? getMediaMediumOrSmallest(post.firstMedia) : undefined}>
                    {!post.hasMedia && '📝'}
                  </PopupPostImage>
                  <PopupPostContent>
                    <PopupPostAuthor>{post.authorUsername || 'Anonymous'}</PopupPostAuthor>
                    {post.text && <PopupPostText>{post.text}</PopupPostText>}
                    <PopupPostTime>{dayjs(post.createdAt).fromNow()}</PopupPostTime>
                  </PopupPostContent>
                </PopupPostPreview>
              ))
            ) : (
              <PopupEmptyPosts>
                {isReached 
                  ? t('challenge.map.noPosts', { defaultValue: 'No posts yet. Be the first!' })
                  : t('challenge.map.notReached', { defaultValue: 'Reach this location to post!' })
                }
              </PopupEmptyPosts>
            )}
          </PopupContent>
          {posts.length > 0 && (
            <PopupViewAll onClick={onViewAllPosts}>
              {t('challenge.map.viewAllPosts', { defaultValue: 'View all {{count}} posts', count: posts.length })}
            </PopupViewAll>
          )}
        </LocationPopup>
      )}
      <LocationMarkerPin 
        isFirst={isFirst} 
        isLast={isLast} 
        isReached={isReached} 
        hasPosts={hasPosts}
        onClick={onTogglePopup}
      >
        {isFirst ? (
          <LocationMarkerEmoji>🚀</LocationMarkerEmoji>
        ) : isLast ? (
          <LocationMarkerEmoji>🏁</LocationMarkerEmoji>
        ) : (
          <LocationMarkerIndex>{index + 1}</LocationMarkerIndex>
        )}
      </LocationMarkerPin>
      {hasPosts && <PostsBadge>{posts.length > 99 ? '99+' : posts.length}</PostsBadge>}
    </LocationMarkerWrapper>
  );
};

// Extracted component to avoid hooks-in-loops for location markers
const MapLocationMarker: React.FC<{
  location: RouteChallengeLocation;
  index: number;
  totalLocations: number;
  isReached: boolean;
  onClick: (location: RouteChallengeLocation, index: number) => void;
}> = observer(({ location, index, totalLocations, isReached, onClick }) => {
  const locationName = useLocalized(location.name) || `Location ${index + 1}`;
  const isFirst = index === 0;
  const isLast = index === totalLocations - 1;

  if (location.position.xorLat === 0 && location.position.yorLng === 0) return null;

  return (
    <AdvancedMarker
      position={{ lat: location.position.xorLat, lng: location.position.yorLng }}
      onClick={() => onClick(location, index)}
      title={locationName}
    >
      <Pin
        background={isFirst ? '#22c55e' : isLast ? '#ef4444' : isReached ? '#00dda2' : '#6b7280'}
        borderColor={isFirst ? '#16a34a' : isLast ? '#dc2626' : isReached ? '#00AF96' : '#4b5563'}
        glyphColor={'#ffffff'}
      />
    </AdvancedMarker>
  );
});

const ChallengeMapContent: React.FC<ChallengeMapProps & { locationPosts: LocationPostsMapType }> = observer(
  ({ challenge, locations, reachedLocations = [], language, onLocationClick, locationPosts }) => {
    const map = useMap();
    const { t } = useTrans();
    const [selectedLocation, setSelectedLocation] = useState<{location: RouteChallengeLocation; index: number} | null>(null);

    const reachedLocationIds = useMemo(() => new Set(reachedLocations.map((r) => r.locationId)), [reachedLocations]);

    const handleMarkerClick = useCallback(
      (location: RouteChallengeLocation, index: number) => {
        setSelectedLocation({ location, index });
      },
      [],
    );

    const handleInfoWindowClose = useCallback(() => {
      setSelectedLocation(null);
    }, []);

    const handleViewAllPosts = useCallback(() => {
      if (selectedLocation && onLocationClick) {
        onLocationClick(selectedLocation.location, selectedLocation.index);
      }
    }, [selectedLocation, onLocationClick]);

    // Get marker label based on state
    const getMarkerLabel = (index: number, isFirst: boolean, isLast: boolean) => {
      if (isFirst) return 'S';
      if (isLast) return 'F';
      return `${index + 1}`;
    };

    return (
      <>
        {/* Route Polylines */}
        {locations.map((location, index) => {
          if (!location.googleMapsRouteToNextLocation) return null;
          const nextLocation = locations[index + 1];
          if (!nextLocation) return null;

          const isReached = reachedLocationIds.has(nextLocation.id);

          try {
            const coordinates = decode(location.googleMapsRouteToNextLocation);
            return (
              <RoutePolyline
                key={`route-${location.id}`}
                coordinates={coordinates}
                isReached={isReached}
              />
            );
          } catch (e) {
            return null;
          }
        })}

        {/* Location Markers */}
        {locations.map((location, index) => (
          <MapLocationMarker
            key={location.id}
            location={location}
            index={index}
            totalLocations={locations.length}
            isReached={reachedLocationIds.has(location.id)}
            onClick={handleMarkerClick}
          />
        ))}

        {/* InfoWindow for selected location */}
        {selectedLocation && (
          <InfoWindow
            position={{ 
              lat: selectedLocation.location.position.xorLat, 
              lng: selectedLocation.location.position.yorLng 
            }}
            onCloseClick={handleInfoWindowClose}
          >
            <InfoWindowContent
              location={selectedLocation.location}
              index={selectedLocation.index}
              totalLocations={locations.length}
              isReached={reachedLocationIds.has(selectedLocation.location.id)}
              posts={locationPosts.get(selectedLocation.location.id) || []}
              language={language}
              onViewAllPosts={handleViewAllPosts}
              t={t}
            />
          </InfoWindow>
        )}

        {/* Stats Overlay - Top Left */}
        <MapOverlay>
          <StatCard>
            <StatEmoji>📍</StatEmoji>
            <StatContent>
              <StatValue>{reachedLocations.length}/{locations.length}</StatValue>
              <StatLabel>Locations</StatLabel>
            </StatContent>
          </StatCard>
        </MapOverlay>

        {/* Bottom Hint */}
        <MapHint>
          <MapHintText>{locations.length} destinations</MapHintText>
          <MapHintDot />
          <MapHintText>Tap markers for details</MapHintText>
        </MapHint>
      </>
    );
  },
);

// InfoWindow content component
const InfoWindowContent: React.FC<{
  location: RouteChallengeLocation;
  index: number;
  totalLocations: number;
  isReached: boolean;
  posts: CommunityWallPost[];
  language: string;
  onViewAllPosts: () => void;
  t: (key: string, options?: any) => string;
}> = ({ location, index, totalLocations, isReached, posts, language, onViewAllPosts, t }) => {
  const isFirst = index === 0;
  const isLast = index === totalLocations - 1;
  const locationName = useLocalized(location.name) || `Location ${index + 1}`;
  const locationImage = location.image ? getMediaMediumOrSmallest(location.image) : undefined;

  return (
    <div style={{ maxWidth: 280, fontFamily: 'system-ui, sans-serif' }}>
      {locationImage && (
        <div style={{ 
          width: '100%', 
          height: 100, 
          backgroundImage: `url(${locationImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          borderRadius: '8px 8px 0 0',
          marginBottom: 8
        }} />
      )}
      <div style={{ padding: '4px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 20 }}>{isFirst ? '🚀' : isLast ? '🏁' : '📍'}</span>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, color: '#333' }}>{locationName}</div>
            <div style={{ fontSize: 12, color: '#888' }}>
              {isReached ? '✅ Reached' : `${location.totalPointsRequired} pts to reach`}
            </div>
          </div>
        </div>
        
        {posts.length > 0 ? (
          <>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 8 }}>
              📸 {posts.length} {posts.length === 1 ? 'post' : 'posts'} at this location
            </div>
            {posts.slice(0, 2).map((post) => (
              <div key={post.id} style={{ 
                display: 'flex', 
                gap: 8, 
                padding: '8px 0',
                borderTop: '1px solid #eee'
              }}>
                {post.hasMedia && post.firstMedia && (
                  <div style={{
                    width: 40,
                    height: 40,
                    borderRadius: 6,
                    backgroundImage: `url(${getMediaMediumOrSmallest(post.firstMedia)})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    flexShrink: 0
                  }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#333' }}>
                    {post.authorUsername || 'Anonymous'}
                  </div>
                  {post.text && (
                    <div style={{ 
                      fontSize: 11, 
                      color: '#666',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {post.text}
                    </div>
                  )}
                </div>
              </div>
            ))}
            <button
              onClick={onViewAllPosts}
              style={{
                width: '100%',
                marginTop: 8,
                padding: '8px 12px',
                background: '#00dda2',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              {t('challenge.map.viewAllPosts', { defaultValue: 'View all posts' })}
            </button>
          </>
        ) : (
          <div style={{ fontSize: 12, color: '#888', textAlign: 'center', padding: '12px 0' }}>
            {isReached 
              ? t('challenge.map.noPosts', { defaultValue: 'No posts yet. Be the first!' })
              : t('challenge.map.notReached', { defaultValue: 'Reach this location to post!' })
            }
          </div>
        )}
      </div>
    </div>
  );
};

const RoutePolyline: React.FC<{ coordinates: number[][]; isReached: boolean }> = ({ coordinates, isReached }) => {
  const map = useMap();

  React.useEffect(() => {
    if (!map || coordinates.length === 0) return;

    const path = coordinates.map(([lat, lng]) => ({ lat, lng }));
    const polyline = new (window as any).google.maps.Polyline({
      path,
      strokeColor: isReached ? Theme.Colors.lightestPrimary : Theme.Colors.primary,
      strokeOpacity: 1,
      strokeWeight: 4,
    });

    polyline.setMap(map);

    return () => {
      polyline.setMap(null);
    };
  }, [map, coordinates, isReached]);

  return null;
};


export const ChallengeMap: React.FC<ChallengeMapProps> = observer((props) => {
  const {
    challenge,
    locations,
    reachedLocations = [],
    language,
    onLocationClick,
    myTeamPosition,
    myParticipantPosition,
    teams = [],
    participants = [],
  } = props;
  const { t } = useTrans();
  const [locationPosts, setLocationPosts] = useState<LocationPostsMapType>(new globalThis.Map());
  const [isSatellite, setIsSatellite] = useState(false);

  // Fetch posts for all locations
  useEffect(() => {
    const fetchLocationPosts = async () => {
      const postsMap = new globalThis.Map<string, CommunityWallPost[]>();
      
      // Fetch posts for each location in parallel
      const fetchPromises = locations.map(async (location) => {
        try {
          const posts = await CommunityWallPostApi.findByLocation(challenge.id, location.id, { size: 10 });
          if (posts.length > 0) {
            postsMap.set(location.id, posts);
          }
        } catch (e) {
          // Silently fail for individual location posts
        }
      });

      await Promise.all(fetchPromises);
      setLocationPosts(postsMap);
    };

    if (locations.length > 0 && challenge.id) {
      fetchLocationPosts();
    }
  }, [challenge.id, locations]);

  // Calculate proper initial region based on ALL locations
  const { mapCenter, zoom } = useMemo(() => {
    // Get all valid location coordinates
    const locationCoords = locations
      .filter((l) => l.position.xorLat !== 0 || l.position.yorLng !== 0)
      .map((l) => ({ lat: l.position.xorLat, lng: l.position.yorLng }));

    // If challenge has explicit map center, use it
    if (challenge.mapCenter && challenge.mapCenter.latitude !== 0) {
      const geo = averageGeolocation(locationCoords);
      // Calculate zoom from the delta
      const zoomFromDelta = Math.min(
        Math.log2(360 / geo.lngDelta),
        Math.log2(180 / geo.latDelta),
      );
      return {
        mapCenter: { lat: challenge.mapCenter.latitude, lng: challenge.mapCenter.longitude },
        zoom: Math.max(2, Math.min(15, Math.floor(zoomFromDelta))),
      };
    }

    // Calculate from all locations
    if (locationCoords.length > 0) {
      const geo = averageGeolocation(locationCoords);

      // If challenge is active and we have our position, center on us
      const myPosition = myTeamPosition || myParticipantPosition;
      if (challenge.started && !challenge.ended && myPosition) {
        // Calculate zoom from the delta
        const zoomFromDelta = Math.min(
          Math.log2(360 / geo.lngDelta),
          Math.log2(180 / geo.latDelta),
        );
        return {
          mapCenter: myPosition,
          zoom: Math.max(2, Math.min(12, Math.floor(zoomFromDelta))),
        };
      }

      // Calculate zoom from the delta
      const zoomFromDelta = Math.min(
        Math.log2(360 / geo.lngDelta),
        Math.log2(180 / geo.latDelta),
      );

      return {
        mapCenter: geo.center,
        zoom: Math.max(2, Math.min(15, Math.floor(zoomFromDelta))),
      };
    }

    return { mapCenter: { lat: 47.3732, lng: 8.5283 }, zoom: 10 };
  }, [challenge, locations, myTeamPosition, myParticipantPosition]);

  const hasLocationsWithCoords = locations.some((l) => l.position.xorLat !== 0 || l.position.yorLng !== 0);

  if (!GOOGLE_MAPS_API_KEY || !hasLocationsWithCoords) {
    return (
      <NoMapFallback>
        <NoMapEmoji>🗺️</NoMapEmoji>
        <NoMapText>
          {!GOOGLE_MAPS_API_KEY
            ? t('challenge.map.noApiKey', { defaultValue: 'Map not available' })
            : t('challenge.map.noLocations', { defaultValue: 'No locations to display' })}
        </NoMapText>
      </NoMapFallback>
    );
  }

  return (
    <div>
      <MapHeader>
        <MapTitle>🗺️ {t('challenge.map.title', { defaultValue: 'ROUTE MAP' })}</MapTitle>
        <MapToggle isActive={isSatellite} onClick={() => setIsSatellite(!isSatellite)}>
          🛰️ {isSatellite ? 'Standard' : 'Satellite'}
        </MapToggle>
      </MapHeader>
      <MapWrapper>
        <MapContainer>
          <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
            <Map
              defaultCenter={mapCenter}
              defaultZoom={zoom}
              mapId="DEMO_MAP_ID"
              mapTypeId={isSatellite ? 'hybrid' : 'roadmap'}
              style={{ width: '100%', height: '100%' }}
              disableDefaultUI={true}
              zoomControl={true}
              gestureHandling="cooperative"
            >
              <ChallengeMapContent {...props} locationPosts={locationPosts} />

              {/* My Team Marker */}
              {myTeamPosition && (
                <AdvancedMarker position={myTeamPosition} title="My Team">
                  <Pin background="#00dda2" borderColor="#00AF96" glyphColor="#fff" />
                </AdvancedMarker>
              )}

              {/* My Participant Marker */}
              {myParticipantPosition && (
                <AdvancedMarker position={myParticipantPosition} title="Me">
                  <Pin background="#00dda2" borderColor="#00AF96" glyphColor="#fff" />
                </AdvancedMarker>
              )}

              {/* Other Teams Markers */}
              {teams.map((team) => (
                <AdvancedMarker key={team.id} position={team.position} title={team.name}>
                  <Pin background="#00C3FF" borderColor="#0094C1" glyphColor="#fff" />
                </AdvancedMarker>
              ))}

              {/* Other Participants Markers */}
              {participants.map((participant) => (
                <AdvancedMarker key={participant.id} position={participant.position} title={participant.name}>
                  <Pin background="#8b5cf6" borderColor="#7c3aed" glyphColor="#fff" />
                </AdvancedMarker>
              ))}
            </Map>
          </APIProvider>
        </MapContainer>
      </MapWrapper>
    </div>
  );
});
